import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProfileState } from '@/contexts/ProfileContext';
import { useCountUp } from '@/components/AnimatedSection';

/* ================================================================================
   Skills Profile & Bridging Your Gaps
   -----------------------------------------------------------------------------
   Two-section page:
     1) Skills Profile — dark radar card (left) + 3 insight cards (right)
     2) Bridging Your Gaps — time-to-competitive banner + filterable gap cards
   ================================================================================ */

const TIER_BANDS = [
  { min: 9,   label: 'Exceptional' },
  { min: 7.5, label: 'Strong' },
  { min: 6,   label: 'Solid' },
  { min: 0,   label: 'Developing' },
];
function tierFor(score) {
  const s = Number(score) || 0;
  return (TIER_BANDS.find(b => s >= b.min) || TIER_BANDS[TIER_BANDS.length - 1]).label;
}
function pointsToNext(score) {
  const s = Number(score) || 0;
  const next = [6, 7.5, 9, 10].find(t => t > s);
  if (next == null) return 0;
  return Math.max(0.5, Math.round((next - s) * 2) / 2);
}

// Split the LLM's "detail" string on sentence boundaries so we can render
// evidence | market | unlocks separately. Requires 3+ lowercase letters
// immediately before the period so abbreviations like "e.g." don't split.
function splitDetail(text) {
  const parts = String(text || '').split(/(?<=[a-z]{3}\.)\s+(?=[A-Z])/).filter(Boolean);
  return {
    evidence: parts[0] || String(text || ''),
    market:   parts[1] || '',
    unlocks:  parts.slice(2).join(' ') || '',
  };
}

export default function SkillsProfile() {
  const { analysis } = useProfileState();
  const c1 = analysis?.call1 || {};
  const web = c1.skill_web || [];
  const [sel, setSel] = useState(0);
  const [gapFilter, setGapFilter] = useState('all');

  const skill = web[sel];
  const { evidence, market, unlocks } = splitDetail(skill?.detail);
  const pts = pointsToNext(skill?.score);

  // If the skill already sits at the top tier (>= 9) OR there is no
  // meaningful level-up sentence, switch the third insight card from
  // "Level up +X points" to a "Maintain at peak" treatment. This
  // avoids the ugly empty-dash state the user reported when the LLM
  // didn't return an unlocks sentence for an already-exceptional
  // skill.
  const score = Number(skill?.score || 0);
  const isPeak = score >= 9;
  const hasUnlocks = Boolean(unlocks && unlocks.trim());
  const showMaintain = isPeak || !hasUnlocks;
  const maintainBody = hasUnlocks
    ? unlocks
    : isPeak
      ? "This skill already sits at the top of the market. Keep it visible — publish, teach, or lead one signature piece each quarter so recruiters and peers keep seeing the proof."
      : "Add one concrete new artefact here — a shipped project, a talk given, or a metric you owned — to lift this score into the next tier.";
  const maintainLabel = showMaintain ? 'Maintain at peak' : `Level up +${pts} points`;
  const maintainClass = showMaintain ? 'sd-insight-card--maintain' : 'sd-insight-card--levelup';

  const gapItems = useMemo(() => {
    const crit = (c1.gaps_critical  || []).map(g => ({ ...g, severity: 'critical' }));
    const imp  = (c1.gaps_important || []).map(g => ({ ...g, severity: 'important' }));
    const all = [...crit, ...imp];
    if (gapFilter === 'critical')  return all.filter(x => x.severity === 'critical');
    if (gapFilter === 'important') return all.filter(x => x.severity === 'important');
    return all;
  }, [c1, gapFilter]);

  return (
    <div className="sd-skills-page">
      {/* ==== SECTION 1: Skills Profile ==== */}
      <header className="mb-6">
        <h1 className="text-[28px] md:text-[30px] font-semibold text-[color:var(--sp-ink)] tracking-tight heading-tight">Skills Profile</h1>
        <p className="text-[color:var(--sp-ink-2)] text-[13.5px] mt-1 max-w-[60ch]">
          Your sustainability skill stack on six axes — plus the specific gaps and the resources to bridge them.
        </p>
      </header>

      <section className="sd-skills-grid mb-10" data-testid="skills-profile-grid">
        {/* LEFT — dark radar card */}
        <div className="sd-skill-web" data-testid="skill-web-card">
          {web.length >= 3 ? (
            <SkillWeb data={web} selected={sel} onSelect={setSel} />
          ) : (
            <div className="text-white/60 text-[13px]">No skill data yet.</div>
          )}
        </div>

        {/* RIGHT — three insight cards */}
        {skill ? (
          <aside className="sd-insight-stack" key={sel} data-testid="skill-insights">
            <div className="sd-insight-card sd-insight-card--evidence animate-fade-in">
              <div className="sd-insight-lbl">Your evidence</div>
              <div className="sd-insight-body">{evidence || '—'}</div>
            </div>
            <div className="sd-insight-card animate-fade-in">
              <div className="sd-insight-lbl">Market position</div>
              <div className="sd-insight-body">{market || '—'}</div>
            </div>
            <div className={`sd-insight-card ${maintainClass} animate-fade-in`} data-testid="skill-insight-levelup">
              <div className="sd-insight-lbl">{maintainLabel}</div>
              <div className="sd-insight-body">{maintainBody}</div>
            </div>
          </aside>
        ) : (
          <div className="sd-insight-card text-[color:var(--sp-muted)] text-[13px]">
            Select a skill on the web to see your evidence, market position and next-level unlocks.
          </div>
        )}
      </section>

      {/* ==== SECTION 2: Bridging Your Gaps ==== */}
      {c1.time_to_competitive && (
        <div className="sd-time-banner mb-6" data-testid="time-to-competitive">
          <div className="sd-time-banner-lbl">Time to competitive</div>
          <div className="sd-time-banner-val">{c1.time_to_competitive}</div>
          <p className="sd-time-banner-desc">
            With your stated hours per week, this is roughly how long until you'd be a credible candidate for the role you're targeting.
          </p>
        </div>
      )}

      <div className="mb-2">
        <h2 className="text-[24px] md:text-[26px] font-semibold text-[color:var(--sp-ink)] tracking-tight heading-tight">Bridging Your Gaps</h2>
        <p className="text-[color:var(--sp-ink-2)] text-[11.5px] mt-1 max-w-[60ch]">
          The specific resources you need to bridge the gap to your target role.
        </p>
      </div>

      {/* Filter pills */}
      <div className="sd-gap-filters" role="tablist" aria-label="Filter gaps by priority">
        {[
          { k: 'all',       label: 'All gaps' },
          { k: 'critical',  label: 'Critical only' },
          { k: 'important', label: 'Important only' },
        ].map(f => (
          <button
            key={f.k}
            type="button"
            role="tab"
            aria-selected={gapFilter === f.k}
            className={`sd-gap-filter ${gapFilter === f.k ? 'is-active' : ''}`}
            onClick={() => setGapFilter(f.k)}
            data-testid={`gap-filter-${f.k}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Gap cards */}
      <div className="space-y-3" data-testid="gap-list">
        {gapItems.length === 0 ? (
          <div className="sd-gap-card text-center text-[color:var(--sp-muted)] text-[12px] italic" style={{ borderLeftColor: 'var(--sp-hairline)' }}>
            No gaps flagged in this category.
          </div>
        ) : (
          gapItems.map((g, i) => <GapCard key={`${gapFilter}-${i}`} g={g} idx={i} />)
        )}
      </div>
    </div>
  );
}

/* ============================================================
   SkillWeb — dark radar chart with animated center score badge
   ============================================================ */

function SkillWeb({ data, selected, onSelect }) {
  const N = data.length;
  const SIZE = 460;
  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const MAX = 10;
  const RADIUS = SIZE * 0.34; // data radius (leaves room for labels)
  const LABEL_R = RADIUS + 34;

  const angleFor = (i) => (-Math.PI / 2) + (i * (2 * Math.PI) / N);
  const point = (i, value) => {
    const r = (value / MAX) * RADIUS;
    return [cx + r * Math.cos(angleFor(i)), cy + r * Math.sin(angleFor(i))];
  };

  // Data polygon points
  const polyPoints = data.map((d, i) => point(i, d.score || 0).join(',')).join(' ');

  // Background rings — 5 concentric decagon-ish polygons
  const rings = [2, 4, 6, 8, 10];
  const ringPoints = (val) => data.map((_, i) => point(i, val).join(',')).join(' ');

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: SIZE,
        aspectRatio: '1 / 1',
      }}
    >
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        width="100%"
        height="100%"
        className="select-none"
        role="img"
        aria-label={`Skill web — six axes, ${data[selected]?.axis || ''} selected`}
      >
        <defs>
          <filter id="sd-skill-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="sd-skill-dotglow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="3.5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background rings */}
        {rings.map((r, i) => (
          <polygon
            key={i}
            points={ringPoints(r)}
            fill="none"
            stroke="rgba(255,255,255,.07)"
            strokeWidth="1"
          />
        ))}

        {/* Spokes */}
        {data.map((_, i) => {
          const [x, y] = point(i, MAX);
          return (
            <line
              key={i}
              x1={cx} y1={cy}
              x2={x} y2={y}
              stroke="rgba(255,255,255,.05)"
              strokeWidth="1"
            />
          );
        })}

        {/* Data polygon — animated on selection change */}
        <polygon
          points={polyPoints}
          fill="rgba(76,217,100,.20)"
          stroke="#4CD964"
          strokeWidth="1.8"
          filter="url(#sd-skill-glow)"
          style={{ transition: 'points 500ms cubic-bezier(.2,.8,.2,1)' }}
        />

        {/* Axis dots (interactive) */}
        {data.map((d, i) => {
          const [x, y] = point(i, d.score || 0);
          const isSel = selected === i;
          return (
            <circle
              key={i}
              cx={x} cy={y}
              r={isSel ? 6.5 : 4.5}
              fill={isSel ? '#F0FFF6' : '#4CD964'}
              stroke={isSel ? '#4CD964' : 'none'}
              strokeWidth={isSel ? 2 : 0}
              filter="url(#sd-skill-dotglow)"
              style={{ cursor: 'pointer', transition: 'r 260ms cubic-bezier(.2,.8,.2,1), fill 260ms' }}
              onClick={() => onSelect(i)}
              tabIndex={0}
              role="button"
              aria-label={`Select ${d.axis}, score ${d.score}`}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(i); } }}
              data-testid={`axis-dot-${i}`}
            />
          );
        })}

        {/* Axis labels — single unified element per axis; the selected one
            styles itself as a green pill, others as plain text. Using ONE
            <g> per axis (instead of an if/else between <text> and <g>)
            prevents any React unmount/mount race or browser-cached ghost
            element from ever duplicating the label. */}
        {data.map((d, i) => {
          const [lx, ly] = [
            cx + LABEL_R * Math.cos(angleFor(i)),
            cy + LABEL_R * Math.sin(angleFor(i)),
          ];
          const isSel = selected === i;
          const label = d.axis;
          const w = Math.max(60, label.length * 6.6 + 22);
          const h = 20;
          return (
            <g
              key={i}
              className={`sd-skill-axis-item ${isSel ? 'is-selected' : ''}`}
              onClick={() => onSelect(i)}
              tabIndex={0}
              role="button"
              aria-label={`Select ${d.axis}`}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(i); } }}
              data-testid={`axis-label-${i}`}
            >
              <rect
                x={lx - w / 2}
                y={ly - h / 2}
                width={w}
                height={h}
                rx={h / 2}
                className="sd-skill-axis-item__pill"
              />
              <text
                x={lx}
                y={ly + 3.5}
                textAnchor="middle"
                className="sd-skill-axis-item__text"
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Center score badge — overlaid via absolute positioning */}
      <CenterScoreBadge score={data[selected]?.score ?? 0} />
    </div>
  );
}

/* Center dark badge with animated ring sweep + counted number + pop */
function CenterScoreBadge({ score }) {
  const target = Math.max(0, Math.min(10, Number(score) || 0));
  const displayed = useCountUp(target, { duration: 700, enabled: true });
  const [pop, setPop] = useState(false);

  // Pop animation on each score change
  useEffect(() => {
    setPop(true);
    const t = setTimeout(() => setPop(false), 260);
    return () => clearTimeout(t);
  }, [target]);

  // Ring geometry
  const size = 104;
  const stroke = 4;
  const r = (size - stroke) / 2 - 3;
  const c = 2 * Math.PI * r;
  const pct = target / 10;
  const offset = c - c * pct;

  const tier = tierFor(target);

  return (
    <div className={`sd-skill-badge ${pop ? 'is-pop' : ''}`} data-testid="skill-center-badge">
      {/* Ring SVG on top of the dark disc */}
      <svg
        width={size} height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ position: 'absolute', inset: 0 }}
        aria-hidden
      >
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="rgba(255,255,255,.08)"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="#4CD964"
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 700ms cubic-bezier(.2,.8,.2,1)' }}
        />
      </svg>
      <div className="sd-skill-badge-inner" style={{ position: 'relative', zIndex: 1 }}>
        <div className="sd-skill-badge-score">{displayed.toFixed(displayed % 1 === 0 ? 1 : 1)}</div>
        <div className="sd-skill-badge-tier">{tier}</div>
      </div>
    </div>
  );
}

/* ============================================================
   Gap card (bridging your gaps)
   ============================================================ */

function ChevronIcon({ open }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
         style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 220ms cubic-bezier(.2,.8,.2,1)' }} aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function ExternalIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      <polyline points="15 3 21 3 21 9"/>
      <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  );
}

function googleSearchUrl(...parts) {
  const q = parts.filter(Boolean).join(' ').trim();
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

function GapCard({ g, idx }) {
  const [open, setOpen] = useState(false);
  const isCrit = g.severity === 'critical';

  // Prefer the LLM's verified direct URL; fall back to a Google search only if truly missing.
  const hasValidUrl = g.url && /^https?:\/\//i.test(g.url) && !/[?&](search|q|query)=|\/search(\/|\?|$)/i.test(g.url);
  const openUrl = hasValidUrl ? g.url : googleSearchUrl(g.resource, g.provider);

  return (
    <article
      className={`sd-gap-card ${isCrit ? 'sd-gap-card--critical' : 'sd-gap-card--important'}`}
      data-testid={`gap-card-${idx}`}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="sd-gap-title">{g.gap}</h3>
        <span
          className={`sd-gap-priority ${isCrit ? 'sd-gap-priority--critical' : 'sd-gap-priority--important'}`}
        >
          {isCrit ? 'Critical' : 'Important'}
        </span>
      </div>

      <div className="sd-gap-meta">
        <div>
          <strong>Resource:</strong> {g.resource || '—'}
          {g.provider && <>  ·  <strong>Provider:</strong> {g.provider}</>}
        </div>
        {(g.cost || g.duration) && (
          <div>
            {g.cost && <><strong>Cost:</strong> {g.cost}</>}
            {g.cost && g.duration && '  ·  '}
            {g.duration && <><strong>Duration:</strong> {g.duration}</>}
          </div>
        )}
      </div>

      <div className="sd-gap-actions">
        <a
          href={openUrl}
          target="_blank"
          rel="noreferrer"
          className="sd-gap-btn-primary"
          data-testid={`gap-open-resource-${idx}`}
        >
          <ExternalIcon />
          Open resource
        </a>
        {g.why && (
          <button
            type="button"
            className="sd-gap-btn-secondary"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            aria-controls={`gap-why-${idx}`}
            data-testid={`gap-why-toggle-${idx}`}
          >
            <ChevronIcon open={open} />
            {open ? 'Hide why' : 'Why this resource?'}
          </button>
        )}
      </div>

      {g.why && (
        <div
          id={`gap-why-${idx}`}
          className={`sd-gap-why ${open ? 'is-open' : ''}`}
        >
          <div className="sd-gap-why-inner">{g.why}</div>
        </div>
      )}
    </article>
  );
}
